export type BookingIdentitySource = {
  organiser?: string | null;
  itsNo?: string | null;
  requesterName?: string | null;
  requesterItsNo?: string | null;
  sadarat?: string | null;
};

export type BookingIdentityField = {
  label: string;
  value: string;
};

/** Label for hydrated sadarat (owned by RazaEvent, not the booking row). */
export const BOOKING_SADARAT_FROM_EVENT_LABEL = "Sadarat (from event)";

function trimField(value: string | null | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * True when hydrated raza requester identity is present and differs from hall organiser.
 * Name compare is case-insensitive; ITS is exact after trim.
 */
export function bookingRequesterDiffersFromOrganiser(
  source: BookingIdentitySource | undefined | null
): boolean {
  if (!source) return false;
  const organiser = trimField(source.organiser);
  const itsNo = trimField(source.itsNo);
  const requesterName = trimField(source.requesterName);
  const requesterItsNo = trimField(source.requesterItsNo);

  if (!requesterName && !requesterItsNo) return false;

  if (requesterName && organiser.toLowerCase() !== requesterName.toLowerCase()) {
    return true;
  }
  if (requesterItsNo && itsNo !== requesterItsNo) {
    return true;
  }
  return false;
}

/** Organiser rows; dual-labels requester when hall organiser differs from raza requester. */
export function resolveBookingOrganiserSummaryFields(
  source: BookingIdentitySource | undefined | null
): BookingIdentityField[] {
  const organiser = trimField(source?.organiser);
  const itsNo = trimField(source?.itsNo);
  const requesterName = trimField(source?.requesterName);
  const requesterItsNo = trimField(source?.requesterItsNo);

  if (bookingRequesterDiffersFromOrganiser(source)) {
    return [
      { label: "Hall organiser", value: organiser || "—" },
      { label: "Organiser ITS", value: itsNo || "—" },
      { label: "Raza requester", value: requesterName || "—" },
      { label: "Requester ITS", value: requesterItsNo || "—" },
    ];
  }

  return [
    { label: "Organizer", value: organiser || "—" },
    { label: "ItsNo", value: itsNo || "—" },
  ];
}

/** Sadarat row with provenance label (hydrated from linked raza event). */
export function resolveBookingSadaratSummaryField(
  source: BookingIdentitySource | undefined | null
): BookingIdentityField {
  const sadarat = trimField(source?.sadarat);
  return {
    label: BOOKING_SADARAT_FROM_EVENT_LABEL,
    value: sadarat || "-",
  };
}

/** One-line sadarat caption for compact summaries (e.g. raza workspace booking card). */
export function formatBookingSadaratFromEventLine(
  sadarat: string | null | undefined
): string | null {
  const value = trimField(sadarat);
  if (!value) return null;
  return `${BOOKING_SADARAT_FROM_EVENT_LABEL}: ${value}`;
}
